import Event from "./Event";
import Optimonk from "./Optimonk";
import Cache from "./Cache";

class Cart extends Event {
    constructor() {
        super();
        this.cart = {};
        this.cacheDuration = 60; // seconds
        this.promises = {
            fetching: null
        };
        this.setUpdatedd = Date.now();
        this.cache = new Cache();
        this.optimonk = new Optimonk();

        this.apiURL = "";
        /* eslint-disable no-undef */
        if (process.env.NODE_ENV === "development") {
            this.apiURL = process.env.MIX_API_DEVELOPMENT ? process.env.MIX_API_DEVELOPMENT : "";
        } else {
            this.apiURL = process.env.MIX_API_PRODUCTION ? process.env.MIX_API_PRODUCTION : "";
        }
        /* eslint-enable no-undef */
    }

    // Creating a global instance
    static createInstance() {
        return new this();
    }

    /**
     * Update timestamp
     */
    setUpdated() {
        this.setUpdatedd = Date.now();
    }

    /**
     * Fetch all items from cart
     *
     * Cart returns pending Promise if API request is pending or
     * returns cached data based on this.cacheDuration value
     *
     * @returns {Promise} Promise object with status and data
     */
    fetchCart() {
        this.publish("itemsFetching");

        // Cart is cached
        let cachedCart = this.cache.get("cart");
        if (cachedCart) return Promise.resolve(this.cache.get("cart").data);

        // If fetching is already underway we return promise
        if (this.promises.fetching) return this.promises.fetching;

        this.promises.fetching = fetch(this.apiURL + "/cart")
            .then(response => {
                return response.text().then(text => {
                    return {
                        status: response.status,
                        content: text
                    };
                });
            })
            .then(response => {
                let content = response.status < 300 ? JSON.parse(response.content) : null;

                if (content) {
                    this.setUpdated();
                    this.cart = content;
                    this.cache.set("cart", this.cacheDuration, {
                        status: response.status,
                        content: content
                    });
                    this.publish("itemsFetched");

                    if (
                        typeof content.data != "undefined" &&
                        typeof content.data.cart != "undefined"
                    ) {
                        if (content.data.cart.length == 0) {
                            this.optimonk.clearCart();
                        }
                    }
                }
                this.promises.fetching = null;

                return {
                    status: response.status,
                    content: content
                };
            })
            .catch(error => {
                this.setUpdated();
                this.promises.fetching = null;
                return {
                    status: 600,
                    error: error
                };
            });

        return this.promises.fetching;
    }

    /**
     * Add items to cart by array of ids
     *
     * @param {Array} itemsArray - Array with book IDs to add
     * @returns {Promise} Promise object with status and current cart
     */
    addItems(itemsArray) {
        this.publish("itemsAdding", itemsArray);

        return fetch(this.apiURL + "/cart", {
            method: "POST",
            cache: "no-cache",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-TOKEN": document.head.querySelector("meta[name=csrf-token]").content
            },
            referrerPolicy: "no-referrer",
            body: JSON.stringify(itemsArray)
        })
            .then(response =>
                response.text().then(text => ({ status: response.status, content: text }))
            )
            .then(response => {
                const content = response.status < 300 ? JSON.parse(response.content) : null;

                if (content) {
                    this.setUpdated();
                    this.cart = content;
                    this.cache.set("cart", this.cacheDuration, {
                        status: response.status,
                        content: content
                    });

                    // Custom event ping
                    const items = content.data.cart;

                    window.Dibook.ping.addToCart({ items });
                    this.optimonk.addToCart(items[0]);
                }
                this.publish("itemsAdded", {
                    status: response.status,
                    content: content
                });
                this.promises.fetching = null;

                return {
                    status: response.status,
                    content: content
                };
            })
            .catch(error => {
                this.setUpdated();
                this.promises.fetching = null;
                return {
                    status: 600,
                    error: error
                };
            });
    }

    /**
     * Remove items from cart by array of ids
     *
     * @param {Array} itemArray - Array with book IDs to remove
     * @returns {Promise} Promise object with status and current cart
     */
    removeItems(itemArray) {
        this.publish("itemsRemoving", itemArray);

        return fetch(this.apiURL + "/cart", {
            method: "DELETE",
            cache: "no-cache",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-TOKEN": document.head.querySelector("meta[name=csrf-token]").content
            },
            referrerPolicy: "no-referrer",
            body: JSON.stringify(itemArray)
        })
            .then(response => {
                return response.text().then(text => {
                    return {
                        status: response.status,
                        content: text
                    };
                });
            })
            .then(response => {
                let content = response.status < 300 ? JSON.parse(response.content) : null;

                if (content) {
                    this.setUpdated();
                    this.cart = content;
                    this.cache.set("cart", this.cacheDuration, {
                        status: response.status,
                        content: content
                    });

                    // Custom event ping
                    let item = content.data.removed[0];
                    window.Dibook.ping.removeFromCart({ items: content.data.removed });
                    this.optimonk.deleteFromCart(item);
                }
                this.publish("itemsRemoved", {
                    status: response.status,
                    content: content
                });
                this.promises.fetching = null;

                return {
                    status: response.status,
                    content: content
                };
            })
            .catch(error => {
                this.setUpdated();
                this.promises.fetching = null;
                return {
                    status: 600,
                    error: error
                };
            });
    }
}

export default Cart;
